// bootRedirectQuery.js — preserve the live query string + hash across a
// redirect that rebuilds its target from a bare path.
//
// tickets/sonnet.md "auth-page redirects still drop the query, and
// `?claim=credits` is exactly what they drop" — the router ticket
// (opus.md, DONE workspace 6d146d9a) fixed this same class at the shell
// boundary (packages/workspace/functions/bootRedirectQuery.js — THREE call
// sites: app.js's two `el.router(_withLiveQuery('/'), …)` bounces +
// demoRouteGuard.js's `queryHashTail(query, hash)` pre-mount guard) but
// never reached `packages/shared/pages/auth/*` — the auth pages' own
// post-login / post-signup / post-reset / post-invite redirects, all of
// which rebuild a bare target path (`el.router('/signup-confirmation', …)`,
// `el.router('/signin', …)`, etc.) that silently drops `window.location`'s
// query string the same way the shell's bounces used to.
//
// This is a SEPARATE file from packages/workspace/functions/
// bootRedirectQuery.js, not a cross-package import of it: `packages/shared`
// (published as `@symbo-ls/workspace-shared`) is consumed by every app —
// canvas, marketplace, chat, calendar, meet, documents, tickets — not just
// the workspace shell (see packages/canvas/pages/index.js importing these
// exact auth pages via the published package name). A relative import
// reaching OUT of packages/shared into packages/workspace would tie every
// consumer to workspace-shell-internal code it doesn't have, and even a
// same-repo deep subpath into `@symbo-ls/workspace-shared/functions/*` is
// documented as unsafe through the platform's importmap once this project
// is pushed as a library (see packages/workspace/app.js's "Bare
// shared-library import" note, tickets/execution-board.md §1) — so the
// canonical implementation lives once per package boundary, not once
// globally. Same algorithm, same acceptance shape, correct scope.
//
// Used by two shapes across the auth pages:
//   • `withLiveQuery(path)` — reads the OUTGOING (current) window location
//     and appends it to a redirect target rebuilt from a bare path
//     (signin.js's `next || '/'`, signup.js's `/signup-confirmation`,
//     reset-password.js's `/signin`, waitlist-invite.js's `/signup`).
//   • `queryHashTail(query, hash)` — for the one site that already has a
//     parsed query/hash pair in hand (accept-invite.js encodes `fullPath`
//     itself via `encodeURIComponent`, so it doesn't need this shape, but
//     the export exists for parity with the shell + any future call site
//     that receives a router-parsed query object instead of `window`).

export const withLiveQuery = (path, win = typeof window !== 'undefined' ? window : null) => {
  if (!win?.location) return path
  return path + (win.location.search || '') + (win.location.hash || '')
}

export const queryHashTail = (query, hash) => {
  const qs = query && Object.keys(query).length ? '?' + new URLSearchParams(query).toString() : ''
  return qs + (hash || '')
}
